import { CircularProgress, Divider, Typography } from '@mui/joy';
import { Box, Stack } from '@mui/material';
import { CaretCircleLeft, Chats } from '@phosphor-icons/react';
import { useId } from '@react-aria/utils';
import { isEmpty } from 'lodash';
import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ReadyState } from 'react-use-websocket';
import { z } from 'zod';

import {
  useAppDispatch,
  useAppSelector,
} from '../../hooks/global/typedReduxHooks';
import MessagesObject from '../../interfaces/MessagesObject';
import {
  IRFIConversation,
  Sender,
  setActiveRFIConversation,
} from '../../slices/rfiMessageSlice';
import { RFI_STATUS_CLOSED } from '../customer/report/reportDocuments/attachments/requestInformationTab/messageRFITabContent';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { InputField } from '../ui/input';

import useRFIWebSocket from '~/hooks/websocket/useRfiWebsocket';
import { useForm } from '~/utils/forms';

export interface LoadingStates {
  [conversationId: string]: boolean;
}

export interface UnreadMessages {
  [conversationId: string]: number;
}
interface RFIMessagesProps {
  rfiConversation: IRFIConversation;
  sender: Sender;
  recipientRegion?: string;
  displayBackButton?: boolean;
  containerPadding?: number;
}

function MessageCard({ message }: { message: MessagesObject }) {
  return (
    <Card size='sm'>
      <div className='flex items-center justify-between'>
        <Typography level='title-sm' sx={{ fontWeight: 600 }}>
          {message.name}
        </Typography>
        <Typography level='body-sm'>
          {new Date(message.createdAt).toLocaleString('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </Typography>
      </div>
      <Typography level='body-sm' sx={{ wordBreak: 'break-word' }}>
        {message.content}
      </Typography>
    </Card>
  );
}

const schema = z.object({
  message: z.string().trim().min(1, 'Required'),
});
// Memoized to avoid re-rendering when the parent component re-renders
const RFIMessages = memo(
  ({
    rfiConversation,
    displayBackButton,
    sender,
    recipientRegion = '',
  }: RFIMessagesProps) => {
    const dispatch = useAppDispatch();
    const { activeRFIConversation } = useAppSelector(
      (state) => state.rfiMessage,
    );
    const messages = rfiConversation.messages ?? [];

    const { handleSendMessage, readyState, isLoading } = useRFIWebSocket({
      conversation: rfiConversation.id,
      sender:
        sender === Sender.CASE_MANAGER ? Sender.CASE_MANAGER : Sender.RECIPIENT,
      recipientId:
        sender === Sender.CASE_MANAGER
          ? rfiConversation.recipient_id
          : rfiConversation.case_manager_id,
      recipientRegion,
    });

    const formId = useId();
    const form = useForm({ schema, defaultValues: { message: '' } });
    const onSubmitHandler = form.handleSubmit((data) => {
      handleSendMessage(data.message);
      form.reset();
    });

    if (!rfiConversation) {
      return null;
    }

    if (activeRFIConversation !== rfiConversation.id) {
      return null;
    }

    if (
      activeRFIConversation === rfiConversation.id &&
      readyState !== ReadyState.OPEN &&
      isLoading
    ) {
      return (
        <Box textAlign='center'>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Card size='md'>
        {displayBackButton && (
          <div className='flex items-center'>
            <Button
              color='secondary'
              variant='plain'
              onClick={() => {
                dispatch(setActiveRFIConversation(''));
              }}
              startDecorator={<CaretCircleLeft weight='fill' />}
              sx={{ p: 0, minHeight: 'auto' }}
            >
              <FormattedMessage defaultMessage={'Back'} />
            </Button>
          </div>
        )}
        {!displayBackButton && (
          <Typography level='title-md'>
            <FormattedMessage defaultMessage='Messages' />
          </Typography>
        )}
        <Divider />
        <div className='flex-1'>
          {isLoading && (
            <div className='flex justify-center'>
              <Typography
                level='body-sm'
                startDecorator={<CircularProgress size='sm' />}
              >
                <FormattedMessage defaultMessage='Fetching messages' />
              </Typography>
            </div>
          )}
          {!isLoading && (
            <ul className='overflow-y-auto grid gap-2 h-72'>
              {messages.length > 0 &&
                messages.map((message, index) => (
                  <MessageCard key={index} message={message} />
                ))}
            </ul>
          )}
          {isEmpty(messages) ||
            (!messages && (
              <Stack
                width='100%'
                height='100%'
                justifyContent='center'
                alignContent='center'
                sx={{
                  color: (theme) => theme.palette.neutralBlackBg[500],
                }}
              >
                <Stack direction='row' justifyContent='center'>
                  <Chats size={91} />
                </Stack>
                <Stack direction='row' justifyContent='center'>
                  <FormattedMessage defaultMessage="Let's begin a conversation..." />
                </Stack>
              </Stack>
            ))}
        </div>
        {rfiConversation.status === RFI_STATUS_CLOSED && (
          <div className='text-center'>
            <Typography level='title-md'>
              <FormattedMessage defaultMessage='This RFI is closed.' />
            </Typography>
          </div>
        )}
        <Divider />
        <form id={formId} onSubmit={onSubmitHandler} className='flex gap-4'>
          <div className='flex-1'>
            <InputField
              control={form.control}
              field='message'
              disabled={
                readyState !== ReadyState.OPEN ||
                rfiConversation.status === RFI_STATUS_CLOSED
              }
            />
          </div>
          <Button
            form={formId}
            disabled={
              readyState !== ReadyState.OPEN ||
              rfiConversation.status === RFI_STATUS_CLOSED
            }
            color='secondary'
            type='submit'
          >
            <FormattedMessage defaultMessage='Send' />
          </Button>
        </form>
      </Card>
    );
  },
);

RFIMessages.displayName = 'RFIMessages';

export default RFIMessages;
